import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MessageSquare, Send, X, PhoneCall, Bot } from "lucide-react";

// Ephemeral session id — new one per page load; server uses it for context threading.
function newSessionId() { return `sarthi-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`; }

const INITIAL_MSG = {
  role: "assistant",
  text: "Hi! I can answer quick questions about our Engineering Consultancy services (Thermal Power, Industrial Systems, C&I, etc.). What would you like to know?",
};

export default function TechSupportChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([INITIAL_MSG]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(newSessionId());
  const listRef = useRef(null);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs, open]);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/consultancy/chat", { session_id: sessionRef.current, message: q });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (_e) {
      setMsgs((m) => [...m, { role: "assistant", text: "I couldn't reach the assistant just now. You can request a custom quote and our team will reach out within one business day." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="tech-support-open" onClick={() => setOpen(true)} className="fixed bottom-24 right-4 z-40 flex items-center gap-2 rounded-full bg-sarthi-secondary px-5 py-3 text-sm font-bold text-white shadow-xl hover:brightness-110">
          <MessageSquare className="h-4 w-4" /> Tech Support
        </button>
      )}
      {open && (
        <div className="fixed bottom-24 right-4 z-40 flex h-[420px] w-[340px] max-w-[95vw] flex-col overflow-hidden rounded-2xl border-2 border-sarthi-border bg-white shadow-2xl" data-testid="tech-support-widget">
          <div className="flex items-center justify-between bg-sarthi-secondary px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div>
                <div className="text-sm font-bold">Sarthi Consultancy · Tech Support</div>
                <div className="text-[10px] opacity-80">AI-powered · Pre-sales questions</div>
              </div>
            </div>
            <button data-testid="tech-support-close" onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto bg-sarthi-accent/30 p-3">
            {msgs.map((m, i) => (
              <div key={i} className={`mb-2 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-sarthi-primary text-white" : "bg-white text-sarthi-text"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="mb-2 text-xs text-sarthi-muted">Assistant is typing…</div>}
          </div>
          <div className="border-t-2 border-sarthi-border p-2">
            <div className="mb-2 flex justify-end">
              <a href="mailto:contact@sarthiassociates.com?subject=Talk%20to%20a%20human" data-testid="talk-human" className="inline-flex items-center gap-1 text-[11px] font-bold text-sarthi-secondary hover:text-sarthi-primary">
                <PhoneCall className="h-3 w-3" /> Talk to a human
              </a>
            </div>
            <div className="flex gap-2">
              <input data-testid="tech-support-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about calibration, loop check, MOV…" className="w-full rounded-xl border-2 border-sarthi-border px-3 py-2 text-sm focus:border-sarthi-primary focus:outline-none" />
              <button data-testid="tech-support-send" onClick={send} disabled={busy || !text.trim()} className="sarthi-btn-primary px-3 py-2 text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
